/**
 * Search strictness (1–100) on the client: bands and copy for the Settings
 * slider. The server owns behavior (functions/utils/searchStrictness.js); the
 * band edges here must match it (33 / 67).
 */
export const STRICTNESS_MIN = 1;
export const STRICTNESS_MAX = 100;
export const STRICTNESS_DEFAULT = 50;
export const EXACT_MAX = 33;
export const BROAD_MIN = 67;

export const STRICTNESS_BANDS = [
    {
        key: 'exact', label: 'Exact', from: STRICTNESS_MIN, to: EXACT_MAX, color: '#60a5fa',
        summary: 'Only what you typed. No stand-in markets or nearby lines — if nothing matches, you see an empty result.',
    },
    {
        key: 'balanced', label: 'Balanced', from: EXACT_MAX + 1, to: BROAD_MIN - 1, color: '#a78bfa',
        summary: 'The default. When nothing matches, we try a close market or line and label the swap.',
    },
    {
        key: 'broad', label: 'Broad', from: BROAD_MIN, to: STRICTNESS_MAX, color: '#fbbf24',
        summary: 'Casts a wider net: more related markets, lines further from yours, and our best guess for ambiguous team names.',
    },
];

export function clampStrictness(v) {
    // Same as the server's normalizeStrictness: missing/blank = the default.
    if (v === null || v === undefined || v === '') return STRICTNESS_DEFAULT;
    const n = Number(v);
    if (!Number.isFinite(n)) return STRICTNESS_DEFAULT;
    return Math.min(STRICTNESS_MAX, Math.max(STRICTNESS_MIN, Math.round(n)));
}

export function strictnessBandFor(v) {
    const s = clampStrictness(v);
    return STRICTNESS_BANDS.find((b) => s >= b.from && s <= b.to) || STRICTNESS_BANDS[1];
}

/** Value for a pointer at `x` px on a track `width` px wide. */
export function strictnessAt(x, width) {
    if (!(width > 0)) return STRICTNESS_DEFAULT;
    const t = Math.min(1, Math.max(0, x / width));
    return clampStrictness(STRICTNESS_MIN + t * (STRICTNESS_MAX - STRICTNESS_MIN));
}

/** 0–1 position of a value on the track. */
export function strictnessFraction(v) {
    return (clampStrictness(v) - STRICTNESS_MIN) / (STRICTNESS_MAX - STRICTNESS_MIN);
}

/** Keyboard step: arrows ±1, Shift+arrows / PageUp/Down ±10, Home/End to the ends. null = not ours. */
export function strictnessForKey(v, key, shift = false) {
    const s = clampStrictness(v);
    const step = shift ? 10 : 1;
    switch (key) {
        case 'ArrowRight': case 'ArrowUp': return clampStrictness(s + step);
        case 'ArrowLeft': case 'ArrowDown': return clampStrictness(s - step);
        case 'PageUp': return clampStrictness(s + 10);
        case 'PageDown': return clampStrictness(s - 10);
        case 'Home': return STRICTNESS_MIN;
        case 'End': return STRICTNESS_MAX;
        default: return null;
    }
}
